import {
    isPublicationCardItemWithVideo,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'

import React from 'react'
import { BaseCardProps } from '../Card'
import { CardLinkWrapper, StyledHeadline } from './ArticleCard.styled'
import {
    CardBodyWrapper,
    Teaser,
    ReadMore,
    LargeCardMedia,
    CardContainer,
    Byline,
} from '../components'
import { getByline, getTopicLink } from '../utils'
import {
    EditorialType,
    getEditorialType,
    getFundingType,
} from '../../../templates'
import { TNLiveComponent } from '../../../content'
import { FundingLabel } from '../../../advertising/FundingLabel/FundingLabel'
import { BylineFundingTypeWrapper } from '../../../publication/Byline/components/TheNightly/Byline.styled'
import { PublishedTime } from '../components/card-body/PublishedTime'
import { StyledReadMoreWrapper } from '../components/card-body/ReadMore.styled'

export interface ArticleCardProps extends BaseCardProps<'article-card'> {
    isStandalonePage?: boolean
    isStandaloneCard?: boolean
}

export const ArticleCard = ({
    item,
    onEvent,
    isStandalonePage,
    adTargeting,
    disableImageLazyLoad,
}: Omit<ArticleCardProps, 'kind'>) => {
    const classificationEnabled = useFeature('story-classification')

    const { loaded: isLoaded } = item
    const result = isLoaded ? item.result : undefined
    const articleLink = toLinkWithHint(item)
    const byline = getByline(item)
    const topicLink = getTopicLink(item)
    const editorialType = isLoaded
        ? getEditorialType(item.result, classificationEnabled)
        : undefined
    const fundingType = isLoaded
        ? getFundingType(item.result, classificationEnabled)
        : undefined
    const showNormalClassification = editorialType !== EditorialType.Live
    const inlineVideoEnabled = useFeature('tn-inline-video-enabled')
    const willPlayVideoInline =
        isLoaded && inlineVideoEnabled
            ? isPublicationCardItemWithVideo(item.result)
            : false

    return (
        <CardContainer>
            {willPlayVideoInline ? (
                <LargeCardMedia
                    item={item}
                    onEvent={onEvent}
                    willPlayVideoInline={willPlayVideoInline}
                    adUnitPath={adTargeting?.adUnitPath}
                    disableImageLazyLoad={disableImageLazyLoad}
                />
            ) : (
                <CardLinkWrapper to={articleLink}>
                    {editorialType && !showNormalClassification && (
                        <TNLiveComponent
                            overlayOnImage={true}
                            showBackground={true}
                        />
                    )}
                    <LargeCardMedia
                        item={item}
                        onEvent={onEvent}
                        willPlayVideoInline={willPlayVideoInline}
                        adUnitPath={adTargeting?.adUnitPath}
                        disableImageLazyLoad={disableImageLazyLoad}
                    />
                </CardLinkWrapper>
            )}

            {result && (
                <CardBodyWrapper>
                    <BylineFundingTypeWrapper>
                        <Byline
                            showClassification={showNormalClassification}
                            editorialType={editorialType}
                            topicLink={topicLink}
                            topicTitle={result.primaryTopic.title}
                            bylineText={byline.text}
                            bylineLink={byline.link}
                            allowCommenting={result.allowCommenting}
                            articleUrl={result._self}
                        />
                        {fundingType && (
                            <FundingLabel
                                fundingType={fundingType}
                                linkToDisclaimer={true}
                            >
                                {fundingType}
                            </FundingLabel>
                        )}
                    </BylineFundingTypeWrapper>
                    <CardLinkWrapper to={articleLink}>
                        <StyledHeadline
                            as={isStandalonePage ? 'h2' : undefined}
                        >
                            {result.shortHeadline}
                        </StyledHeadline>
                        <Teaser>{result.teaser}</Teaser>
                        <StyledReadMoreWrapper>
                            <PublishedTime
                                publicationDate={result.publicationDate}
                            />
                            <ReadMore readTime={result.readTimeMinutes} />
                        </StyledReadMoreWrapper>
                    </CardLinkWrapper>
                </CardBodyWrapper>
            )}
        </CardContainer>
    )
}
